/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { Suspense, lazy } from 'react';

import { Navigate, Route, BrowserRouter as Router, Routes } from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ChatSidebarProvider } from '@/contexts/ChatSidebarContext';
import { useChatSidebar } from '@/contexts/useChatSidebar';
import { AuthProvider } from '@/hooks/useAuth';
import { SubscriptionProvider } from '@/hooks/useSubscription';
import { cn } from '@/lib/utils';

import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';

import { CookieManagerProvider } from '@/components/CookieManagerProvider';
import DevelopmentRoute from '@/components/DevelopmentRoute';
import { GTMPageViewTracker } from '@/components/GTMPageViewTracker';
import LoggedOutRoute from '@/components/LoggedOutRoute';
import { MaintenanceMode } from '@/components/MaintenanceMode';
import { ModeProvider } from '@/components/ModeProvider';
import { PostHogProvider } from '@/components/PostHogProvider';
import ProtectedRoute from '@/components/ProtectedRoute';
import { RouterScrollToTop } from '@/components/RouterScrollToTop';
import { ChatSidebar } from '@/components/chat/ChatSidebar';
import { ChatSidebarTrigger } from '@/components/chat/ChatSidebarTrigger';
import { LearningModuleChatRedirect } from '@/components/routing/LearningModuleChatRedirect';
import { SplitScreenModuleRedirect } from '@/components/routing/SplitScreenModuleRedirect';
import { SplitScreenRedirect } from '@/components/routing/SplitScreenRedirect';
import { UserPresenceProvider } from '@/components/user-presence/UserPresenceProvider';

import { ErrorBoundary } from './components/ErrorBoundary';
import { MaintenanceBanner } from './components/MaintenanceBanner';
import { HasAccessToCourses } from './components/courses/HasAccessToCourses';

const AccessibilityStatement = lazy(() =>
  import('@/pages/public/AccessibilityStatement').then((module) => ({
    default: module.AccessibilityStatement,
  }))
);
const Account = lazy(() =>
  import('@/pages/Account').then((module) => ({
    default: module.Account,
  }))
);
const Admin = lazy(() =>
  import('@/pages/Admin').then((module) => ({ default: module.Admin }))
);
const AppVersion = lazy(() => import('@/pages/AppVersion'));
const Authentication = lazy(() =>
  import('@/pages/public/Authentication').then((module) => ({
    default: module.Authentication,
  }))
);
const BusinessModelCanvas = lazy(() =>
  import('@/pages/BusinessModelCanvas').then((module) => ({
    default: module.BusinessModelCanvasPage,
  }))
);
const Chat = lazy(() =>
  import('@/pages/Chat').then((module) => ({
    default: module.Chat,
  }))
);
const ComponentsLibrary = lazy(() =>
  import('@/pages/ComponentsLibrary').then((module) => ({
    default: module.ComponentsLibrary,
  }))
);
const CustomerJourneyMap = lazy(() =>
  import('@/pages/CustomerJourneyMap').then((module) => ({
    default: module.CustomerJourneyMap,
  }))
);
const CookiePolicy = lazy(() =>
  import('@/pages/public/CookiePolicy').then((module) => ({
    default: module.CookiePolicy,
  }))
);
const CustomerProfile = lazy(() =>
  import('@/pages/CustomerProfile').then((module) => ({
    default: module.CustomerProfile,
  }))
);
const CustomerPersona = lazy(() =>
  import('@/pages/CustomerPersona').then((module) => ({
    default: module.CustomerPersona,
  }))
);
const Home = lazy(() =>
  import('@/pages/Home').then((module) => ({ default: module.Home }))
);
const ExperimentDetail = lazy(() =>
  import('@/pages/ExperimentDetail').then((module) => ({
    default: module.ExperimentDetailPage,
  }))
);
const ExperimentNew = lazy(() => import('@/pages/ExperimentNew'));
const Experiments = lazy(() =>
  import('@/pages/Experiments').then((module) => ({
    default: module.Experiments,
  }))
);
const FiveWhys = lazy(() =>
  import('@/pages/FiveWhys').then((module) => ({
    default: module.FiveWhys,
  }))
);
const HypothesisDetail = lazy(() =>
  import('@/pages/HypothesisDetail').then((module) => ({
    default: module.HypothesisDetail,
  }))
);
const Insights = lazy(() =>
  import('@/pages/Insights').then((module) => ({
    default: module.Insights,
  }))
);
const Invitation = lazy(() =>
  import('@/pages/public/Invitation').then((module) => ({
    default: module.Invitation,
  }))
);
const ResetPassword = lazy(() =>
  import('@/pages/public/ResetPassword').then((module) => ({
    default: module.ResetPassword,
  }))
);
const KnownsUnknowns = lazy(() =>
  import('@/pages/KnownsUnknowns').then((module) => ({
    default: module.KnownsUnknowns,
  }))
);
const LeanCanvas = lazy(() => import('@/pages/LeanCanvas'));
const OpportunitySolutionTree = lazy(() =>
  import('@/pages/OpportunitySolutionTree').then((module) => ({
    default: module.OpportunitySolutionTree,
  }))
);
const Learning = lazy(() =>
  import('@/pages/Learning').then((module) => ({
    default: module.Learning,
  }))
);
const LearningContent = lazy(() =>
  import('@/pages/LearningContent').then((module) => ({
    default: module.LearningContent,
  }))
);
const LearningModuleDetail = lazy(() =>
  import('@/pages/LearningModuleDetail').then((module) => ({
    default: module.LearningModuleDetail,
  }))
);
const Courses = lazy(() => import('@/pages/private/courses/Courses'));
const CourseDetail = lazy(() => import('@/pages/private/courses/CourseDetail'));
const CourseModuleDetail = lazy(
  () => import('@/pages/private/courses/CourseModuleDetail')
);
const CourseLesson = lazy(() => import('@/pages/private/courses/CourseLesson'));
const CourseQuiz = lazy(() => import('@/pages/private/courses/CourseQuiz'));
const CourseAssessment = lazy(
  () => import('@/pages/private/courses/CourseAssessment')
);
const CourseTool = lazy(() => import('@/pages/private/courses/CourseTool'));
const MarketResearch = lazy(() =>
  import('@/pages/MarketResearch').then((module) => ({
    default: module.MarketResearch,
  }))
);
const NotFound = lazy(() => import('@/pages/NotFound'));
const OAuthContinue = lazy(() =>
  import('@/pages/public/OAuthContinue').then((module) => ({
    default: module.OAuthContinue,
  }))
);
const OAuthTasks = lazy(() =>
  import('@/pages/public/OAuthTasks').then((module) => ({
    default: module.OAuthTasks,
  }))
);
const AuthSetup = lazy(() =>
  import('@/pages/AuthSetup').then((module) => ({ default: module.AuthSetup }))
);
const Onboarding = lazy(() =>
  import('@/pages/Onboarding').then((module) => ({
    default: module.Onboarding,
  }))
);
const PrivacyPolicy = lazy(() =>
  import('@/pages/public/PrivacyPolicy').then((module) => ({
    default: module.PrivacyPolicy,
  }))
);
const ProductDetail = lazy(() =>
  import('@/pages/ProductDetail').then((module) => ({
    default: module.ProductDetail,
  }))
);
const ProductFeedback = lazy(() =>
  import('@/pages/ProductFeedback').then((module) => ({
    default: module.ProductFeedback,
  }))
);
const ProductStrategyCanvas = lazy(() =>
  import('@/pages/ProductStrategyCanvas').then((module) => ({
    default: module.ProductStrategyCanvas,
  }))
);
const ProductToolVersions = lazy(() =>
  import('@/pages/ProductToolVersions').then((module) => ({
    default: module.ProductToolVersions,
  }))
);
const ValuePropositionCanvas = lazy(() =>
  import('@/pages/ValuePropositionCanvas').then((module) => ({
    default: module.ValuePropositionCanvas,
  }))
);
const Products = lazy(() =>
  import('@/pages/Products').then((module) => ({
    default: module.Products,
  }))
);
const Roadmap = lazy(() =>
  import('@/pages/Roadmap').then((module) => ({
    default: module.Roadmap,
  }))
);
const SSOCallback = lazy(() =>
  import('@/pages/public/SSOCallback').then((module) => ({
    default: module.SSOCallback,
  }))
);
const SubscriptionCancelled = lazy(() =>
  import('@/pages/SubscriptionCancelled').then((module) => ({
    default: module.SubscriptionCancelled,
  }))
);
const SubscriptionSuccess = lazy(() =>
  import('@/pages/SubscriptionSuccess').then((module) => ({
    default: module.SubscriptionSuccess,
  }))
);
const TermsAndConditions = lazy(() =>
  import('@/pages/public/TermsAndConditions').then((module) => ({
    default: module.TermsAndConditions,
  }))
);
const Tokens = lazy(() =>
  import('@/pages/Tokens').then((module) => ({ default: module.Tokens }))
);
const TokensVerification = lazy(() =>
  import('@/pages/TokensVerification').then((module) => ({
    default: module.TokensVerification,
  }))
);
const TokensVerificationContent = lazy(() =>
  import('@/pages/TokensVerificationContent').then((module) => ({
    default: module.TokensVerificationContent,
  }))
);
const ToolVersions = lazy(() =>
  import('@/pages/ToolVersions').then((module) => ({
    default: module.ToolVersions,
  }))
);
const Tools = lazy(() =>
  import('@/pages/Tools').then((module) => ({ default: module.Tools }))
);
const UserStoryMap = lazy(() =>
  import('@/pages/UserStoryMap').then((module) => ({
    default: module.UserStoryMap,
  }))
);
const VerifyEmail = lazy(() =>
  import('@/pages/public/VerifyEmail').then((module) => ({
    default: module.VerifyEmail,
  }))
);

const queryClient = new QueryClient();

const AppContent = () => {
  const { isOpen, mode, sidebarWidth } = useChatSidebar();
  const shouldPushContent = isOpen && mode === 'sidebar';

  return (
    <div
      className={cn(
        'min-h-screen transition-all duration-200',
        shouldPushContent && 'h-screen overflow-hidden'
      )}
      style={{
        paddingRight: shouldPushContent ? `${sidebarWidth}px` : 0,
      }}
    >
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center" />
        }
      >
        <Routes>
          <Route path="/" element={<Navigate to="/auth" replace />} />
          <Route
            path="/auth"
            element={
              <LoggedOutRoute>
                <Authentication />
              </LoggedOutRoute>
            }
          />
          <Route
            path="/invitation"
            element={
              <LoggedOutRoute>
                <Invitation />
              </LoggedOutRoute>
            }
          />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route path="/contact" element={<Navigate to="/auth" replace />} />
          <Route path="/auth/sso-callback" element={<SSOCallback />} />
          <Route path="/auth/continue" element={<OAuthContinue />} />
          <Route path="/auth/tasks" element={<OAuthTasks />} />
          <Route path="/auth/setup" element={<AuthSetup />} />

          <Route
            path="/contact-enterprise"
            element={<Navigate to="/auth" replace />}
          />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route
            path="/accessibility-statement"
            element={<AccessibilityStatement />}
          />

          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            }
          />
          <Route path="/dashboard" element={<Navigate to="/home" replace />} />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute>
                <Products />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id"
            element={
              <ProtectedRoute>
                <ProductDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id/feedback"
            element={
              <ProtectedRoute>
                <ProductFeedback />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:id/market-research"
            element={
              <ProtectedRoute>
                <MarketResearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:productId/tools/versions"
            element={
              <ProtectedRoute>
                <ProductToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <ProtectedRoute>
                <Roadmap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/:productId/projects/:id"
            element={
              <ProtectedRoute>
                <Roadmap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experiments"
            element={
              <ProtectedRoute>
                <Experiments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experiments/new"
            element={
              <ProtectedRoute>
                <ExperimentNew />
              </ProtectedRoute>
            }
          />
          <Route
            path="/experiments/:id"
            element={
              <ProtectedRoute>
                <ExperimentDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hypotheses/:id"
            element={
              <ProtectedRoute>
                <HypothesisDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights"
            element={
              <ProtectedRoute>
                <Insights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/insights/:id"
            element={
              <ProtectedRoute>
                <Insights />
              </ProtectedRoute>
            }
          />
          <Route
            path="/market-research"
            element={
              <ProtectedRoute>
                <MarketResearch />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning"
            element={
              <ProtectedRoute>
                <Learning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning/:content"
            element={
              <ProtectedRoute>
                <LearningContent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning/modules/:id"
            element={
              <ProtectedRoute>
                <LearningModuleDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/learning/modules/:id/chat"
            element={
              <ProtectedRoute>
                <LearningModuleChatRedirect />
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <Courses />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseDetail />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/modules/:moduleId"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseModuleDetail />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/modules/:moduleId/lessons/:lessonId"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseLesson />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/modules/:moduleId/quizzes/:quizId"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseQuiz />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/assessment"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseAssessment />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/courses/:courseId/modules/:moduleId/lessons/:lessonId/tools/:toolId"
            element={
              <ProtectedRoute>
                <HasAccessToCourses>
                  <CourseTool />
                </HasAccessToCourses>
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools"
            element={
              <ProtectedRoute>
                <Tools />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/business-model-canvas/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/lean-canvas/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-profile/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-persona/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/product-strategy-canvas/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/knowns-unknowns/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/five-whys/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/value-proposition-canvas/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-journey-map/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/opportunity-solution-tree/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/user-story-map/versions"
            element={
              <ProtectedRoute>
                <ToolVersions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-profile/versions/:versionId"
            element={
              <ProtectedRoute>
                <CustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-persona/versions/:versionId"
            element={
              <ProtectedRoute>
                <CustomerPersona />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/business-model-canvas/versions/:versionId"
            element={
              <ProtectedRoute>
                <BusinessModelCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/lean-canvas/versions/:versionId"
            element={
              <ProtectedRoute>
                <LeanCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/product-strategy-canvas/versions/:versionId"
            element={
              <ProtectedRoute>
                <ProductStrategyCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/knowns-unknowns/versions/:versionId"
            element={
              <ProtectedRoute>
                <KnownsUnknowns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/five-whys/versions/:versionId"
            element={
              <ProtectedRoute>
                <FiveWhys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/value-proposition-canvas/versions/:versionId"
            element={
              <ProtectedRoute>
                <ValuePropositionCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-journey-map/versions/:versionId"
            element={
              <ProtectedRoute>
                <CustomerJourneyMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/user-story-map"
            element={
              <ProtectedRoute>
                <UserStoryMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/user-story-map/versions/:versionId"
            element={
              <ProtectedRoute>
                <UserStoryMap />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/opportunity-solution-tree/versions/:versionId"
            element={
              <ProtectedRoute>
                <OpportunitySolutionTree />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tools/customer-profile"
            element={
              <ProtectedRoute>
                <CustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/split-screen" element={<SplitScreenRedirect />} />
          <Route
            path="/split-screen-module"
            element={<SplitScreenModuleRedirect />}
          />
          <Route
            path="/tools/business-model-canvas"
            element={
              <ProtectedRoute>
                <BusinessModelCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/lean-canvas"
            element={
              <ProtectedRoute>
                <LeanCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/product-strategy-canvas"
            element={
              <ProtectedRoute>
                <ProductStrategyCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/knowns-unknowns"
            element={
              <ProtectedRoute>
                <KnownsUnknowns />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/five-whys"
            element={
              <ProtectedRoute>
                <FiveWhys />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/opportunity-solution-tree"
            element={
              <ProtectedRoute>
                <OpportunitySolutionTree />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/value-proposition-canvas"
            element={
              <ProtectedRoute>
                <ValuePropositionCanvas />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-profile"
            element={
              <ProtectedRoute>
                <CustomerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-persona"
            element={
              <ProtectedRoute>
                <CustomerPersona />
              </ProtectedRoute>
            }
          />
          <Route
            path="/customer-persona"
            element={
              <ProtectedRoute>
                <CustomerPersona />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tools/customer-journey-map"
            element={
              <ProtectedRoute>
                <CustomerJourneyMap />
              </ProtectedRoute>
            }
          />

          <Route
            path="/subscription-success"
            element={
              <ProtectedRoute>
                <SubscriptionSuccess />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription-cancelled"
            element={
              <ProtectedRoute>
                <SubscriptionCancelled />
              </ProtectedRoute>
            }
          />

          <Route
            path="/components-library"
            element={
              <DevelopmentRoute>
                <ComponentsLibrary />
              </DevelopmentRoute>
            }
          />
          <Route
            path="/tokens"
            element={
              <DevelopmentRoute>
                <Tokens />
              </DevelopmentRoute>
            }
          />
          <Route
            path="/tokens-verification"
            element={
              <DevelopmentRoute>
                <TokensVerification />
              </DevelopmentRoute>
            }
          />
          <Route
            path="/tokens-verification-content"
            element={
              <DevelopmentRoute>
                <TokensVerificationContent activeTab="verification" />
              </DevelopmentRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/app-version"
            element={
              <ProtectedRoute>
                <AppVersion />
              </ProtectedRoute>
            }
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>

      <ChatSidebarTrigger />
      <ChatSidebar />
    </div>
  );
};

export const App = () => (
  <PostHogProvider>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <MaintenanceMode>
          <Router>
            <ModeProvider>
              <AuthProvider>
                <ChatSidebarProvider>
                  <UserPresenceProvider>
                    <CookieManagerProvider>
                      <GTMPageViewTracker />
                      <RouterScrollToTop />
                      <SubscriptionProvider>
                        <TooltipProvider>
                          <AppContent />
                        </TooltipProvider>
                        <Toaster />
                        <MaintenanceBanner />
                      </SubscriptionProvider>
                    </CookieManagerProvider>
                  </UserPresenceProvider>
                </ChatSidebarProvider>
              </AuthProvider>
            </ModeProvider>
          </Router>
        </MaintenanceMode>
      </QueryClientProvider>
    </ErrorBoundary>
  </PostHogProvider>
);
